import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, TextField, Button, Stack, Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { getSettings, updateSettings } from '../../api/endpoints/settings';

const GeneralSettings = () => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await getSettings();
      setForm(res.data.data || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      for (const [key, value] of Object.entries(form)) {
        await updateSettings(key, value);
      }
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const labelMap = {
    appName: 'Application Name',
    supportEmail: 'Support Email',
    contactPhone: 'Contact Phone',
    minRecharge: 'Minimum Recharge Amount',
    currencySymbol: 'Currency Symbol Position',
    referralBonus: 'Referral Bonus Amount',
    freeChatMinutes: 'Free Chat Minutes on Signup',
    otpExpiry: 'OTP Expiry (seconds)',
    maxLoginAttempts: 'Max Login Attempts',
  };

  const typeMap = {
    supportEmail: 'email',
    minRecharge: 'number',
    referralBonus: 'number',
    freeChatMinutes: 'number',
    otpExpiry: 'number',
    maxLoginAttempts: 'number',
  };

  const selectFields = ['currencySymbol'];

  return (
    <Box>
      <PageHeader title="General Settings" subtitle="Configure application settings" />
      <form onSubmit={handleSave}>
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              {Object.entries(form).map(([key, value]) => (
                <Grid item xs={12} md={6} key={key}>
                  {selectFields.includes(key) ? (
                    <FormControl fullWidth>
                      <InputLabel>{labelMap[key] || key}</InputLabel>
                      <Select value={value || ''} label={labelMap[key] || key} onChange={handleChange(key)}>
                        <MenuItem value="left">Left (₹ 100)</MenuItem>
                        <MenuItem value="right">Right (100 ₹)</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      label={labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                      type={typeMap[key] || 'text'}
                      value={value || ''}
                      onChange={handleChange(key)}
                      fullWidth
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" type="submit" startIcon={<SaveIcon />} disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default GeneralSettings;